import { isUndefined, head } from 'lodash-es';
import * as t from 'io-ts';
import { zip } from '@bayer-int/portletce-shared';

import type {
    DrillDefinition,
    InteractiveTitleSelectDefinition,
    InteractiveTitleSettingDefinition,
    InteractiveTitleStaticStringDefinition,
} from 'header/types';
import type { BWQuery } from 'portlet/models/query';
import { isBWQuery } from 'portlet/models/query';
import { createActiveDrillRespectingAutoDrillHierarchiesFinder } from 'portlet/models/query/systems/bw';

import { define, createSpecSettingsInterface } from './_common';

export interface DrillSettingDefinition extends DrillDefinition {
    key: 'DRILL';
}

const definition: DrillSettingDefinition = {
    type: 'Drill',
    key: 'DRILL',
    allowMultiple: false,
    label: 'drill',
    options: [],
    value: [],
};

const specSettingsInterface = createSpecSettingsInterface(definition, t.array(t.string));

export const codec = specSettingsInterface.input;

export const DRILL_BY_NONE_OPTION: DrillDefinition['options'][number] = {
    key: 'None',
    label: 'None',
};

export const DrillSettingDefinition = define({
    key: 'DRILL',
    label: 'drill',
    definition,
    fromSetting: (settings, spec, view) => {
        const drillValues = specSettingsInterface.extractValue(settings.drill);
        const query = view?.query || spec.query;

        if (!query || !isBWQuery(query) || isUndefined(drillValues)) {
            return;
        }
        const value = query.querySettings.allowMultipleDrills
            ? drillValues
            : drillValues.slice(0, 1);

        return isUndefined(value)
            ? undefined
            : value.map((key, i) => ({
                  key,
                  order: i,
                  // note: we set `drill` to the same value as `key` which holds true for 99% of cases
                  // but is not true for autodrill hierarchies (where `drill` should be a particular
                  // level of the hierarchy based on the current filters applied to the query).
                  // the correct value of `drill` for autodrill hierarchies is later set via `DrillSettingDefinition.applySettingDependency`
                  drill: key,
              }));
    },
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: (settings, spec, view) => {
        if (isUndefined(settings.drill)) return;

        const query = view?.query || spec.query;
        if (!query || !isBWQuery(query)) return;

        const addDrillByNoneOption =
            query.querySettings.enableDrillByNone && !query.querySettings.allowMultipleDrills;
        const autoDrillHierarchies: DrillDefinition['options'] | undefined =
            query.querySettings.autoDrillHierarchies?.map(({ key, label, hierarchy }) => ({
                key,
                label,
                hierarchy,
            }));

        const options = [
            ...(addDrillByNoneOption ? [DRILL_BY_NONE_OPTION] : []),
            ...(autoDrillHierarchies ? autoDrillHierarchies : []),
            ...getDrillOptions(query.drills),
        ];

        return {
            ...definition,
            ...specSettingsInterface.createDefinitionWithSettings(settings.drill),
            allowMultiple: query.querySettings.allowMultipleDrills,
            options,
        };
    },

    applySettingDependency: ({
        diff,
        specification,
        currentDefinition,
        view,
        settings,
        filters,
        listenToSignalMapping,
    }) => {
        const query = view?.query || specification.query;
        const currentDrill = settings.DRILL;
        const viewChanged = !!diff.VIEWID;

        if (!query || !currentDrill || !isBWQuery(query)) return;

        // on view change (and possibly query change),
        // check if selected drills are still valid,
        // if all fit, we keep all of them,
        // otherwise we discard all of them (by unsetting the setting)
        const areAllDrillsStillValid = currentDrill.every(drill =>
            currentDefinition.options.some(({ key }) => key === drill.key),
        );

        if (viewChanged && !areAllDrillsStillValid) return { value: undefined };

        // on each filter and filter change we update the `drill` property to respect the autodrill feature
        // see:
        //      packages/portletce/src/portlet/models/query/systems/bw.ts
        //      Query.QuerySettings.autoDrillHierarchies in packages/portletce-docs/src/config-reference/portlet-config.md
        const findActiveDrill = createActiveDrillRespectingAutoDrillHierarchiesFinder(
            settings,
            filters,
            view?.signals || [],
            query,
            listenToSignalMapping,
        );

        const updatedDrill = currentDrill.map(value => ({
            ...value,
            drill: findActiveDrill(value.key),
        }));
        const haveDrillsChanged = zip(currentDrill, updatedDrill).some(
            ([{ drill: drillA }, { drill: drillB }]) => drillA !== drillB,
        );

        return haveDrillsChanged
            ? {
                  value: updatedDrill,
              }
            : undefined;
    },

    replacementTags: definition => ({
        DRILL:
            definition.value
                ?.map(({ key }) => definition.options.find(opt => opt.key === key)?.label)
                .join(', ') || '',
    }),

    interactiveTags: definition => {
        const notSupported: InteractiveTitleSettingDefinition<
            DrillSettingDefinition,
            InteractiveTitleStaticStringDefinition
        > = {
            setting: definition,
            field: {
                type: 'StaticString',
                value: 'NOT SUPPORTED WITH MULTIPLE DRILLS',
            },
            format: () => definition,
        };

        const currentValue = definition.value && head(definition.value);
        const drillTag: InteractiveTitleSettingDefinition<
            DrillSettingDefinition,
            InteractiveTitleSelectDefinition
        > = {
            setting: definition,
            field: {
                type: 'Select',
                value: currentValue ? currentValue.key : '',
                options: definition.options,
            },
            format: key => ({
                ...definition,
                value: key !== null ? [{ key, drill: key, order: 0 }] : null,
            }),
        };

        return {
            DRILL: definition.allowMultiple ? notSupported : drillTag,
        };
    },

    toSignals: definition => {
        const labelsByKey = Object.fromEntries(
            definition.options.map(({ key, label }) => [key, label]),
        );
        const valuesWithLabels = definition.value
            ? definition.value
                  .map(drill => ({
                      ...drill,
                      label: labelsByKey[drill.key] || drill.drill,
                  }))
                  .sort(({ order: orderA }, { order: orderB }) => orderA - orderB)
            : definition.value;

        return [{ name: definition.key, value: valuesWithLabels }];
    },
});

function getDrillOptions(drills: BWQuery['drills']): DrillSettingDefinition['options'] {
    return (
        drills
            //undefined drillable is assumed to be true. 'char' -> characteristic
            .filter(drill => drill.drillable !== false && drill.type === 'char')
            .map(({ key, label }) => ({ key, label: label === '' ? key : label }))
    );
}
